import { z } from 'zod';

/**
 * Environment Configuration Schema
 * 
 * Type-safe, validated environment variables with:
 * - Runtime validation (catches missing vars at startup)
 * - Type inference (full TypeScript support)
 * - Default values where appropriate
 * - Clear error messages
 */

/**
 * Strict boolean env parser.
 *
 * NOT `z.coerce.boolean()` — that is `Boolean(value)`, so every non-empty string
 * is `true`: `"false"` → true, `"0"` → true. A flag using it can only ever be
 * disabled by *omitting* it, which makes `FLAG="false"` more enabling than
 * setting nothing. This accepts only the literal strings and fails loud on a
 * typo like `"1"` instead of silently enabling.
 *
 * The preprocess step is load-bearing: `.default()` only fires on `undefined`,
 * but the shared Dockerfile's `ENV FLAG=${FLAG}` bakes an empty string whenever
 * the build arg isn't passed. Without this, such a build throws here at module
 * load and white-screens the whole app. Empty means unset; a real typo still throws.
 */
const envBool = (fallback: boolean) =>
  z
    .preprocess(
      (v) => (v === '' ? undefined : v),
      z.enum(['true', 'false']).default(fallback ? 'true' : 'false'),
    )
    .transform((v) => v === 'true');

const envSchema = z.object({
  // Deployment environment (consistent with monorepo)
  VITE_ENVIRONMENT: z.enum(['development', 'staging', 'production']).default('production'),
  MODE: z.string().default('development'),
  DEV: z.boolean().default(false),
  PROD: z.boolean().default(false),
  
  // API Configuration
  VITE_API_BASE_URL: z.string().url({
    message: 'VITE_API_BASE_URL must be a valid URL'
  }),
  VITE_API_VERSION: z.string().default('/v1'),
  // Feature Flags
  VITE_ENABLE_MONITORING: envBool(true),
  VITE_ENABLE_DEV_TOOLS: envBool(false),
  
  // Optional: Analytics
  VITE_ANALYTICS_ID: z.string().optional(),
});

/**
 * Parse and validate environment variables
 * Throws descriptive error if validation fails
 */
function parseEnv() {
  const parsed = envSchema.safeParse({
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_API_VERSION: import.meta.env.VITE_API_VERSION,
    VITE_ENABLE_MONITORING: import.meta.env.VITE_ENABLE_MONITORING,
    VITE_ENABLE_DEV_TOOLS: import.meta.env.VITE_ENABLE_DEV_TOOLS,
    VITE_ANALYTICS_ID: import.meta.env.VITE_ANALYTICS_ID,
  });

  if (!parsed.success) {
    console.error('❌ Invalid environment variables:');
    console.error(parsed.error.flatten().fieldErrors);
    throw new Error('Invalid environment variables. Check console for details.');
  }

  return parsed.data;
}

/**
 * Type-safe environment configuration
 * Use throughout the app instead of import.meta.env
 */
export const env = parseEnv();

/**
 * Derived environment helpers
 */
export const isDevelopment = env.VITE_ENVIRONMENT === 'development';
export const isStaging = env.VITE_ENVIRONMENT === 'staging';
export const isProduction = env.VITE_ENVIRONMENT === 'production';

/**
 * Should we enable internal development tools and features?
 * Returns true for development and staging, false for production
 * 
 * Use this to conditionally show/enable:
 * - Pre-filled form values for testing
 * - Debug information and dev tools
 * - Test user shortcuts and internal features
 * - Performance metrics and internal UI
 * 
 * @example
 * ```tsx
 * if (enableInternalTools()) {
 *   formData.email = 'test@example.com';
 * }
 * ```
 */
export const enableInternalTools = (): boolean => {
  return isDevelopment || isStaging;
};

/**
 * Whether to mount dev tooling — the DevTools panel AND its MSW auth-mock worker.
 * Single source of truth so the two can't drift (the worker must only run where
 * the panel that controls it is available).
 *
 * Both halves are load-bearing:
 * - `VITE_ENABLE_DEV_TOOLS` lets an environment that *could* show the tools turn
 *   them off (i.e. staging). This only works because the flag is parsed by
 *   `envBool`, not `z.coerce.boolean()` — see the note there.
 * - `import.meta.env.DEV || isStaging` restricts *where* they can appear at all.
 *   DEV covers local (`vite dev`) without requiring VITE_ENVIRONMENT to be set;
 *   isStaging covers deployed staging builds, where DEV is always false.
 *
 * Production fails closed twice over: VITE_ENVIRONMENT defaults to 'production'
 * when unset, and the flag defaults to false.
 */
export const SHOW_DEV_TOOLS = env.VITE_ENABLE_DEV_TOOLS && (import.meta.env.DEV || isStaging);

/**
 * API Configuration
 */
export const API_CONFIG = {
  baseUrl: env.VITE_API_BASE_URL,
  version: env.VITE_API_VERSION,
  fullUrl: `${env.VITE_API_BASE_URL}${env.VITE_API_VERSION}`,
} as const;

/**
 * Feature Flags
 */
export const FEATURES = {
  monitoring: env.VITE_ENABLE_MONITORING,
  devTools: env.VITE_ENABLE_DEV_TOOLS,
} as const;
